import { ethers } from 'ethers';
import { toast } from 'react-hot-toast';

// Contract ABIs
import FTTFTokenABI from '@/contracts/FTTFToken.json';
import dUSDTokenABI from '@/contracts/dUSDToken.json';
import FTTFPassABI from '@/contracts/FTTFPass.json';
import FTTFDropABI from '@/contracts/FTTFDrop.json';
import FTTFMarketplaceABI from '@/contracts/FTTFMarketplace.json';
import TippingSystemABI from '@/contracts/TippingSystem.json';
import TokenGateABI from '@/contracts/TokenGate.json';

// Contract addresses from environment variables
const FTTF_TOKEN_ADDRESS = import.meta.env.VITE_FTTF_TOKEN_ADDRESS;
const DUSD_TOKEN_ADDRESS = import.meta.env.VITE_DUSD_TOKEN_ADDRESS;
const FTTF_PASS_ADDRESS = import.meta.env.VITE_FTTF_PASS_ADDRESS;
const FTTF_DROP_ADDRESS = import.meta.env.VITE_FTTF_DROP_ADDRESS;
const MARKETPLACE_ADDRESS = import.meta.env.VITE_MARKETPLACE_ADDRESS;
const TIPPING_SYSTEM_ADDRESS = import.meta.env.VITE_TIPPING_SYSTEM_ADDRESS;
const TOKEN_GATE_ADDRESS = import.meta.env.VITE_TOKEN_GATE_ADDRESS;

class Web3Service {
  private provider: ethers.BrowserProvider | null = null;
  private signer: ethers.JsonRpcSigner | null = null;

  // Contract instances
  private fttfToken: ethers.Contract | null = null;
  private dUSDToken: ethers.Contract | null = null;
  private fttfPass: ethers.Contract | null = null;
  private fttfDrop: ethers.Contract | null = null;
  private marketplace: ethers.Contract | null = null;
  private tippingSystem: ethers.Contract | null = null;
  private tokenGate: ethers.Contract | null = null;

  constructor() {
    this.initProvider();
  }

  private async initProvider() {
    if (window.ethereum) {
      try {
        this.provider = new ethers.BrowserProvider(window.ethereum);
        
        // Listen for account changes
        window.ethereum.on('accountsChanged', () => {
          this.resetContracts();
          window.location.reload();
        });
        
        // Listen for chain changes
        window.ethereum.on('chainChanged', () => {
          this.resetContracts();
          window.location.reload();
        });
      } catch (error) {
        console.error('Error initializing provider:', error);
      }
    } else {
      console.warn('No Ethereum provider detected');
    }
  }

  private resetContracts() {
    this.signer = null;
    this.fttfToken = null;
    this.dUSDToken = null;
    this.fttfPass = null;
    this.fttfDrop = null;
    this.marketplace = null;
    this.tippingSystem = null;
    this.tokenGate = null;
  }

  async getSigner() {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    if (!this.signer) {
      try {
        this.signer = await this.provider.getSigner();
      } catch (error) {
        console.error('Error getting signer:', error);
        throw error;
      }
    }

    return this.signer;
  }

  async getChainId(): Promise<number> {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    const network = await this.provider.getNetwork();
    return Number(network.chainId);
  }

  // Initialize contract instances
  private async getFTTFToken() {
    if (!this.fttfToken) {
      const signer = await this.getSigner();
      this.fttfToken = new ethers.Contract(FTTF_TOKEN_ADDRESS, FTTFTokenABI, signer);
    }
    return this.fttfToken;
  }

  private async getDUSDToken() {
    if (!this.dUSDToken) {
      const signer = await this.getSigner();
      this.dUSDToken = new ethers.Contract(DUSD_TOKEN_ADDRESS, dUSDTokenABI, signer);
    }
    return this.dUSDToken;
  }

  private async getFTTFPass() {
    if (!this.fttfPass) {
      const signer = await this.getSigner();
      // Use the ABI directly
      const abi = Array.isArray(FTTFPassABI) ? FTTFPassABI : [];
      this.fttfPass = new ethers.Contract(FTTF_PASS_ADDRESS, abi, signer);
    }
    return this.fttfPass;
  }

  private async getFTTFDrop() {
    if (!this.fttfDrop) {
      const signer = await this.getSigner();
      // Use the ABI directly
      const abi = Array.isArray(FTTFDropABI) ? FTTFDropABI : [];
      this.fttfDrop = new ethers.Contract(FTTF_DROP_ADDRESS, abi, signer);
    }
    return this.fttfDrop;
  }

  private async getMarketplace() {
    if (!this.marketplace) {
      const signer = await this.getSigner();
      // Use the ABI directly
      const abi = Array.isArray(FTTFMarketplaceABI) ? FTTFMarketplaceABI : [];
      this.marketplace = new ethers.Contract(MARKETPLACE_ADDRESS, abi, signer);
    }
    return this.marketplace;
  }

  private async getTippingSystem() {
    if (!this.tippingSystem) {
      const signer = await this.getSigner();
      // Use the ABI directly
      const abi = Array.isArray(TippingSystemABI) ? TippingSystemABI : [];
      this.tippingSystem = new ethers.Contract(TIPPING_SYSTEM_ADDRESS, abi, signer);
    }
    return this.tippingSystem;
  }

  private async getTokenGate() {
    if (!this.tokenGate) {
      const signer = await this.getSigner();
      // Use the ABI directly
      const abi = Array.isArray(TokenGateABI) ? TokenGateABI : [];
      this.tokenGate = new ethers.Contract(TOKEN_GATE_ADDRESS, abi, signer);
    }
    return this.tokenGate;
  }
  
  // Helper methods for the frontend
  getNFTContractAddress() {
    return FTTF_PASS_ADDRESS;
  }
  
  getDropContractAddress() {
    return FTTF_DROP_ADDRESS;
  }

  // Token functions
  async getFTTFBalance(address: string): Promise<string> {
    try {
      const token = await this.getFTTFToken();
      const balance = await token.balanceOf(address);
      return ethers.formatUnits(balance, 18);
    } catch (error) {
      console.error('Error getting FTTF balance:', error);
      throw error;
    }
  }

  async getDUSDBalance(address: string): Promise<string> {
    try {
      const token = await this.getDUSDToken();
      const balance = await token.balanceOf(address);
      return ethers.formatUnits(balance, 18);
    } catch (error) {
      console.error('Error getting dUSD balance:', error);
      throw error;
    }
  }

  // NFT functions
  async mintNFT(
    to: string,
    uri: string,
    royaltyReceiver: string,
    royaltyPercentage: number
  ): Promise<string> {
    try {
      const nft = await this.getFTTFPass();
      const tx = await nft.safeMint(to, uri, royaltyReceiver, royaltyPercentage * 100); // Convert percentage to basis points
      
      toast.loading('Minting NFT...', { id: 'mint-nft' });
      
      const receipt = await tx.wait();
      
      toast.success('NFT minted successfully!', { id: 'mint-nft' });
      
      // Find the Transfer event to get the token ID
      const transferEvent = receipt.logs
        .map((log: any) => {
          try {
            return nft.interface.parseLog(log);
          } catch (e) {
            return null;
          }
        })
        .find((event: any) => event && event.name === 'Transfer');
      
      return transferEvent ? transferEvent.args.tokenId.toString() : '';
    } catch (error) {
      console.error('Error minting NFT:', error);
      toast.error('Failed to mint NFT', { id: 'mint-nft' });
      throw error;
    }
  }

  async createDrop(
    creator: string,
    initialSupply: number,
    maxSupply: number,
    uri: string,
    royaltyPercentage: number
  ): Promise<string> {
    try {
      const drop = await this.getFTTFDrop();
      const tx = await drop.createDrop(
        creator,
        initialSupply,
        maxSupply,
        uri,
        royaltyPercentage * 100 // Convert percentage to basis points
      );
      
      toast.loading('Creating Drop...', { id: 'create-drop' });
      
      const receipt = await tx.wait();
      
      toast.success('Drop created successfully!', { id: 'create-drop' });
      
      // Find the event to get the token ID
      const event = receipt.logs
        .map((log: any) => {
          try {
            return drop.interface.parseLog(log);
          } catch (e) {
            return null;
          }
        })
        .find((event: any) => event && event.name === 'TransferSingle');
      
      return event ? event.args.id.toString() : '';
    } catch (error) {
      console.error('Error creating Drop:', error);
      toast.error('Failed to create Drop', { id: 'create-drop' });
      throw error;
    }
  }

  // Marketplace functions
  async listNFTForSale(
    tokenAddress: string,
    tokenId: string,
    price: string,
    isERC721: boolean
  ): Promise<string> {
    try {
      const marketplace = await this.getMarketplace();
      
      // Convert price to wei
      const priceInWei = ethers.parseUnits(price, 18);
      
      let tx;
      if (isERC721) {
        tx = await marketplace.createERC721Listing(tokenAddress, tokenId, priceInWei);
      } else {
        // For ERC1155, we're listing a single token for now
        tx = await marketplace.createERC1155Listing(tokenAddress, tokenId, 1, priceInWei);
      }
      
      toast.loading('Listing NFT for sale...', { id: 'list-nft' });
      
      const receipt = await tx.wait();
      
      toast.success('NFT listed for sale!', { id: 'list-nft' });
      
      // Find the event to get the listing ID
      const event = receipt.logs
        .map((log: any) => {
          try {
            return marketplace.interface.parseLog(log);
          } catch (e) {
            return null;
          }
        })
        .find((event: any) => event && event.name === 'ListingCreated');
      
      return event ? event.args.listingId.toString() : '';
    } catch (error) {
      console.error('Error listing NFT:', error);
      toast.error('Failed to list NFT', { id: 'list-nft' });
      throw error;
    }
  }

  async buyNFT(listingId: string): Promise<boolean> {
    try {
      const marketplace = await this.getMarketplace();
      
      // Get the listing details
      const listing = await marketplace.getListing(listingId);
      
      // Approve the marketplace to spend dUSD
      const dUSD = await this.getDUSDToken();
      await dUSD.approve(MARKETPLACE_ADDRESS, listing.price);
      
      // Buy the NFT
      const tx = await marketplace.buyListing(listingId);
      
      toast.loading('Buying NFT...', { id: 'buy-nft' });
      
      await tx.wait();
      
      toast.success('NFT purchased successfully!', { id: 'buy-nft' });
      
      return true;
    } catch (error) {
      console.error('Error buying NFT:', error);
      toast.error('Failed to buy NFT', { id: 'buy-nft' });
      throw error;
    }
  }

  // Tipping functions
  async sendTip(recipient: string, amount: string): Promise<string> {
    try {
      const tippingSystem = await this.getTippingSystem();
      
      // Convert amount to wei
      const amountInWei = ethers.parseUnits(amount, 18);
      
      // Approve the tipping system to spend dUSD
      const dUSD = await this.getDUSDToken();
      await dUSD.approve(TIPPING_SYSTEM_ADDRESS, amountInWei);
      
      // Send the tip
      const tx = await tippingSystem.sendTip(recipient, amountInWei);
      
      toast.loading('Sending tip...', { id: 'send-tip' });
      
      const receipt = await tx.wait();
      
      toast.success('Tip sent successfully!', { id: 'send-tip' });
      
      return receipt.hash;
    } catch (error) {
      console.error('Error sending tip:', error);
      toast.error('Failed to send tip', { id: 'send-tip' });
      throw error;
    }
  }

  // Token gate functions
  async hasAccess(user: string, contentId: string): Promise<boolean> {
    try {
      const tokenGate = await this.getTokenGate();
      return tokenGate.hasAccess(user, contentId);
    } catch (error) {
      console.error('Error checking access:', error);
      throw error;
    }
  }

  async makePayment(contentId: string): Promise<boolean> {
    try {
      const tokenGate = await this.getTokenGate();
      
      // Get the gate details
      const gateId = await tokenGate.getGateIdForContent(contentId);
      const gate = await tokenGate.getGate(gateId);
      
      // Approve the token gate to spend dUSD
      const dUSD = await this.getDUSDToken();
      await dUSD.approve(TOKEN_GATE_ADDRESS, gate.requiredAmount);
      
      // Make the payment
      const tx = await tokenGate.makePayment(contentId);
      
      toast.loading('Making payment...', { id: 'make-payment' });
      
      await tx.wait();
      
      toast.success('Payment successful!', { id: 'make-payment' });
      
      return true;
    } catch (error) {
      console.error('Error making payment:', error);
      toast.error('Failed to make payment', { id: 'make-payment' });
      throw error;
    }
  }
}

export const web3Service = new Web3Service();